import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customersService } from '@/services/api/customers.service';
import { ApiError } from '@/services/api/client';
import type {
  AddSeatRequest,
  CreateOrganizationRequest,
  CustomerListQuery,
  CustomerPortalUser,
  IssueLicenseRequest,
  ProvisionRequest,
  RevokeSeatMode,
} from '@/types';
import { queryKeys } from './keys';

/**
 * Server state for customers.
 *
 * React Query owns fetching, caching and retry. Nothing here touches Redux -
 * a customer list is server state with a cache lifetime, not something a route
 * guard reads synchronously.
 */

export function useCustomers(query: CustomerListQuery) {
  return useQuery({
    queryKey: queryKeys.customers.list(query),
    queryFn: ({ signal }) => customersService.list(query, signal),
    /*
     * The previous page stays on screen while the next one loads, so typing in
     * the search box does not blank the table on every keystroke.
     */
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.customers.detail(id ?? ''),
    queryFn: ({ signal }) => customersService.get(id ?? '', signal),
    enabled: Boolean(id),
    retry: (failureCount, error) => {
      // A 404 is an answer, not a failure worth retrying.
      if (error instanceof ApiError && (error.status === 404 || error.isAuthFailure)) return false;
      return failureCount < 2;
    },
  });
}

/**
 * The drawer's devices and timeline. Under `customers.all`, so every mutation
 * that already invalidates customers refreshes it too - a revoke shows up in
 * the timeline without anything else having to know the drawer exists.
 */
export function useSeatHistory(organizationId: string, seatId: string | undefined, limit: number) {
  return useQuery({
    queryKey: queryKeys.customers.seatHistory(seatId ?? '', limit),
    queryFn: ({ signal }) =>
      customersService.seatHistory(organizationId, seatId ?? '', limit, signal),
    enabled: Boolean(seatId),
    // Showing older events refetches with a bigger limit; keep the list on
    // screen meanwhile rather than flashing back to a skeleton.
    placeholderData: (previous) => previous,
  });
}

/**
 * Provisioning.
 *
 * Deliberately does NOT invalidate on success by itself - the caller navigates
 * to a result screen showing licence keys and passwords that exist nowhere else,
 * and a refetch racing that render is a needless risk. The list is invalidated
 * when the operator leaves that screen.
 */
export function useProvisionCustomer() {
  return useMutation({
    mutationFn: (body: ProvisionRequest) => customersService.provision(body),
  });
}

/**
 * The sales-rep picker's options.
 *
 * A long staleTime and no refetch-on-window-focus fuss: who is an active staff
 * member changes rarely enough that refetching it every time the dialog opens
 * would just be a round trip nobody asked for.
 */
export function useSalesReps() {
  return useQuery({
    queryKey: queryKeys.staff.salesReps(),
    queryFn: ({ signal }) => customersService.listSalesReps(signal),
    staleTime: 5 * 60_000,
  });
}

/**
 * Creating an organization on its own - no licence, no seats, nothing shown
 * only once. Unlike full provisioning, there is no credentials screen to
 * race, so this invalidates the moment it succeeds rather than waiting for
 * the caller to leave one.
 */
export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateOrganizationRequest) => customersService.createOrganization(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
  });
}

/**
 * A second (or third) licence for an existing organization.
 *
 * Invalidated immediately, not deferred to when the credentials screen
 * closes: that screen renders from the mutation's own result, held in local
 * state, so a background refetch of the list and this org's detail cannot
 * disturb it - and invalidating now means the new licence and its "Plans"
 * count are already correct by the time the operator gets there.
 */
export function useIssueLicense(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: IssueLicenseRequest) => customersService.issueLicense(organizationId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
  });
}

/**
 * One more person on a licence that still has room.
 *
 * Same invalidation reasoning as useIssueLicense: the new key and password
 * are shown from the mutation's own result, so refetching underneath is safe
 * and means the seat roster is already right when the dialog closes.
 */
export function useAddSeat(organizationId: string, licenseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AddSeatRequest) => customersService.addSeat(organizationId, licenseId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
  });
}

/**
 * Releasing the machine a seat is bound to.
 *
 * Invalidated on success because the seat's fingerprint, status and the
 * licence's bound count all change, and all three are on screen.
 */
export function useReleaseSeat(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (seatId: string) => customersService.releaseSeat(organizationId, seatId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
  });
}

/**
 * Taking a seat back.
 *
 * Invalidated estate-wide rather than just this customer: a revoke can delete
 * a seat outright, which moves the organization's seat and licence counts on
 * the list screen as well as the row it was clicked from.
 */
export function useRevokeSeat(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ seatId, mode }: { seatId: string; mode: RevokeSeatMode }) =>
      customersService.revokeSeat(organizationId, seatId, { mode }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
  });
}

/**
 * A replacement key on an existing seat.
 *
 * The new key is read off the mutation's own result to be shown once, and the
 * refetch underneath brings the seat's status and key column into line.
 */
export function useReissueSeat(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (seatId: string) => customersService.reissueSeat(organizationId, seatId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
  });
}

/**
 * Pushing every still-live seat on a licence forward by N months.
 *
 * Invalidated on success: every seat's own expiry and the licence's Validity
 * column both move, and both are on screen.
 */
export function useExtendLicense(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ licenseId, months }: { licenseId: string; months: number }) =>
      customersService.extendLicense(organizationId, licenseId, { months }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
  });
}

/**
 * Emailing a customer what the screen is currently showing.
 *
 * No invalidation: nothing about the customer changed - this only moved a
 * secret the admin was already looking at from the screen to an inbox.
 */
export function useEmailSeatCredentials(organizationId: string) {
  return useMutation({
    mutationFn: ({
      seatId,
      password,
      resend,
    }: {
      seatId: string;
      password?: string;
      /** The welcome mail again, no password - see the server's EmailSeatCredentialsInput. */
      resend?: boolean;
    }) =>
      customersService.emailSeatCredentials(
        organizationId,
        seatId,
        password !== undefined ? { password } : resend ? { resend } : {},
      ),
  });
}

export function useEmailPortalPassword() {
  return useMutation({
    mutationFn: ({ portalUserId, password }: { portalUserId: string; password: string }) =>
      customersService.emailPortalPassword(portalUserId, { password }),
  });
}

export function useSetPortalUserStatus(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      portalUserId,
      status,
    }: {
      portalUserId: string;
      status: CustomerPortalUser['status'];
    }) => customersService.setPortalUserStatus(portalUserId, status),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.customers.detail(organizationId),
      });
    },
  });
}

/**
 * Resetting a password.
 *
 * No invalidation: the response carries a password shown once, and nothing on
 * the detail page changes as a result. Refetching would only risk a re-render
 * that clears the one thing the operator needs to copy.
 */
export function useResetPortalUserPassword() {
  return useMutation({
    mutationFn: ({ portalUserId, password }: { portalUserId: string; password?: string }) =>
      customersService.resetPortalUserPassword(portalUserId, password),
  });
}
